import { HttpErrorResponse } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal } from '@angular/core'
import { Router, RouterOutlet } from '@angular/router'
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular'
import {
    AccountInfo,
    InteractionRequiredAuthError,
    InteractionStatus,
    PopupRequest,
    RedirectRequest,
} from '@azure/msal-browser'
import { UserRolesConditionEnum } from '@ui-core/enums/user-roles.enum'
import { UserRolesEnum } from '@ui-core/enums/user-roles.enum'
import { UserInterface } from '@ui-core/interfaces/user.interface'
import { AlertService } from '@ui-core/services/alert.service'
import { AuthTokenService } from '@ui-core/services/auth-token.service'
import { ErrorService } from '@ui-core/services/error.service'
import { HelpService } from '@ui-core/services/help.service'
import { HttpService } from '@ui-core/services/http.service'
import { MenuService } from '@ui-core/services/menu.service'
import { RouterStateService } from '@ui-core/services/router-state.service'
import { TokenService } from '@ui-core/services/token.service'
import { UserService } from '@ui-core/services/user.service'
import { ErrorTypeEnum } from '@ui-core/ui/error/enum/error.enum'
import { Error } from '@ui-core/ui/error/error'
import { SideMenu } from '@ui-core/ui/side-menu/side-menu'
import { Help } from '@ui-features/help/help'
import { Welcome } from '@ui-features/welcome/welcome'
import { EmptyParamsInterface } from '@ui-shared/interfaces/empty-params.interface'
import { getInitials, urlParseTemplate } from '@ui-utils/url.util'
import { MenuItem, MessageService } from 'primeng/api'
import { ButtonModule } from 'primeng/button'
import { MenubarModule } from 'primeng/menubar'
import { TieredMenuModule } from 'primeng/tieredmenu'
import { Toast } from 'primeng/toast'
import { filter, interval, Subject, switchMap, takeUntil } from 'rxjs'

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, ButtonModule, Toast, TieredMenuModule, MenubarModule, SideMenu, Welcome, Error, Help],
    templateUrl: './app.html',
    providers: [MessageService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnDestroy {
    private alertService = inject(AlertService)
    private authService = inject(MsalService)
    private authTokenService = inject(AuthTokenService)
    private errorService = inject(ErrorService)
    private helpService = inject(HelpService)
    private menuService = inject(MenuService)
    private messageService = inject(MessageService)
    private msalBroadcastService = inject(MsalBroadcastService)
    private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG)
    private routerStateService = inject(RouterStateService)
    private tokenService = inject(TokenService)
    private userService = inject(UserService)
    private httpService = inject(HttpService)
    private router = inject(Router)

    activeAccount = signal<AccountInfo | null>(null)
    idToken = signal<string | null>(null)
    isAdmin = computed(() => this.userService.hasAnyRole([UserRolesEnum.Ballance_Admin]))
    isIframe = signal<boolean>(false)
    isSideMenuCollapsed = this.menuService.getIsCollapsed()
    loginDisplay = signal<boolean>(false)
    loginHint = signal<string | null>(null)
    menuItems = signal<MenuItem[]>([])
    renewalCounter = signal<number>(1)
    showUI = signal<boolean>(false)
    showError = signal<boolean>(false)
    showHelp = this.helpService.getIsHelpVisible()
    title = 'Ballance'
    user = signal<UserInterface | null>(null)
    userInfo = signal<UserInterface | null>(null)
    userMenu = signal<MenuItem[]>([])
    userRoles = signal<string[]>([])
    userIsLoaded = signal<boolean>(false)
    errorType = signal<ErrorTypeEnum>(ErrorTypeEnum.API_ERROR)

    unsubscribe = new Subject<void>()

    constructor() {
        this.initErrorHandler()
            .initAlertHandler()
            .initRouter()
            .initialiseAuth()
            .checkInProgress()
            .loadInitialData()
            .initInterval()
    }

    ngOnDestroy(): void {
        this.unsubscribe.next()
        this.unsubscribe.complete()
    }

    initConfig(): this {
        const menuItems = this.filterMenuByRoles(this.menuService.get()())
        this.menuItems.set(menuItems)

        this.user.set(this.userService.get()())
        this.userRoles.set(this.userService.roles())

        return this
    }

    initErrorHandler(): this {
        effect(() => {
            const error = this.errorService.get()()
            if (error === null) {
                return
            }

            const httpError = error as HttpErrorResponse
            if (
                httpError !== undefined &&
                httpError.status !== undefined &&
                (httpError.status === 401 || httpError.status === 403)
            ) {
                // this.logout()
            }

            this.messageService.add({
                severity: 'error',
                summary: error?.name,
                detail: error?.message,
                life: 5000,
            })
        })

        return this
    }

    initAlertHandler(): this {
        effect(() => {
            const alert = this.alertService.get()()
            if (alert === null) {
                return
            }

            this.messageService.add(alert)
        })

        return this
    }

    initRouter(): this {
        this.routerStateService.subscribeRouterStateChanges()

        return this
    }

    loadInitialData(): this {
        effect(async () => {
            if (!this.userIsLoaded()) return

            const userHasRoles = this.userService.roles()

            if (userHasRoles.length > 0) {
                this.initConfig()
                this.showUI.set(true)

                return
            }

            this.errorType.set(ErrorTypeEnum.NO_ROLES)
            this.showError.set(true)
        })

        return this
    }

    initInterval(): this {
        interval(15000)
            .pipe(switchMap(() => this.validateToken()))
            .subscribe()

        return this
    }

    filterMenuByRoles(menu: MenuItem[]): MenuItem[] {
        menu.forEach((item) => {
            if (
                item.state &&
                item.state['permission'] &&
                item.state['permission']['roles'] &&
                item.state['permission']['condition']
            ) {
                const roles = item.state['permission']['roles'] as UserRolesEnum[]
                const condition = item.state['permission']['condition'] as UserRolesConditionEnum

                if (condition === UserRolesConditionEnum.Any) {
                    item.visible = false

                    if (this.userService.hasAnyRole(roles)) {
                        item.visible = true
                    }
                } else {
                    item.visible = false

                    if (this.userService.hasRoles(roles)) {
                        item.visible = true
                    }
                }
            }
        })

        return menu.filter((item) => item.visible !== false)
    }

    initialiseAuth(): this {
        this.authService.handleRedirectObservable().subscribe()
        this.isIframe.set(window !== window.parent && !window.opener)

        const params = new URLSearchParams(window.location.search)
        this.loginHint.set(params.get('login_hint'))

        return this
    }

    checkInProgress(): this {
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this.unsubscribe)
            )
            .subscribe(() => {
                this.setActiveAccount().checkAndSetActiveAccount()
            })

        return this
    }

    setActiveAccount(): this {
        this.activeAccount.set(this.authService.instance.getActiveAccount())

        if (this.activeAccount() === null && this.authService.instance.getAllAccounts().length > 0) {
            const accounts = this.authService.instance.getAllAccounts()
            this.authService.instance.setActiveAccount(accounts[0])
            this.activeAccount.set(this.authService.instance.getActiveAccount())
        }

        return this
    }

    checkAndSetActiveAccount(): this {
        this.setAcquireTokenSilent().finally(() => {
            this.activateApp().displayLogin()
        })
        return this
    }

    activateApp(): this {
        if (this.activeAccount() !== null) {
            const token = this.tokenService.get()() ?? this.authService.instance.getActiveAccount()?.idToken ?? null
            const roles = this.authService.instance.getActiveAccount()?.idTokenClaims?.roles ?? []

            this.tokenService.set(token)
            this.userService.setRoles(roles)

            const url = urlParseTemplate(this.httpService.getUrl.me, {})

            this.httpService
                .get<EmptyParamsInterface, UserInterface>(url, {})
                .then((data) => {
                    this.showError.set(false)
                    this.userService.set({
                        id: data.id,
                        name: data.name,
                        preferredUsername: data.preferredUsername,
                        isLogged: true,
                        initials: getInitials(data.name),
                    })

                    this.userInfo.set(this.userService.get()())
                    this.userIsLoaded.set(true)
                })
                .catch(() => {
                    this.showError.set(true)
                    this.userService.setInitialised(true)
                    this.router.navigate(['/error'])
                })
        }

        return this
    }

    displayLogin(): this {
        if (this.activeAccount() === null) {
            if (this.isIframe()) {
                this.attemptSsoSilent()
            } else {
                this.loginDisplay.set(true)
                this.userService.setInitialised(true)
            }
        }

        return this
    }

    private attemptSsoSilent(): void {
        const request = {
            scopes: ['openid', 'profile', 'User.Read'],
            loginHint: this.loginHint() ?? undefined,
        }

        this.authService.instance
            .ssoSilent(request)
            .then((result) => {
                this.authService.instance.setActiveAccount(result.account)
                this.activeAccount.set(result.account)
                this.activateApp()
            })
            .catch(() => {
                this.loginDisplay.set(true)
                this.userService.setInitialised(true)
            })
    }

    async setAcquireTokenSilent(): Promise<void> {
        const token = await this.authTokenService.getValidToken()
        this.idToken.set(token)
    }

    async validateToken(): Promise<void> {
        const token =
            this.tokenService.get()() ?? this.idToken() ?? this.authService.instance.getActiveAccount()?.idToken ?? null

        if (this.authTokenService.isTokenExpiringSoon(token) || this.renewalCounter() >= 120) {
            const refreshedToken = await this.authTokenService.getValidToken({
                forceRefresh: this.renewalCounter() >= 120,
            })

            this.idToken.set(refreshedToken)
            this.renewalCounter.set(0)
        } else {
            this.renewalCounter.set(this.renewalCounter() + 1)
        }
    }

    login() {
        const scopes = ['openid', 'profile', 'User.Read']
        const authRequest = {
            ...(this.msalGuardConfig.authRequest ?? {}),
            scopes,
        }

        if (this.isIframe()) {
            this.authService
                .loginPopup({
                    ...authRequest,
                    loginHint: this.loginHint() ?? undefined,
                } as PopupRequest)
                .subscribe({
                    next: (result) => {
                        this.authService.instance.setActiveAccount(result.account)
                        this.activeAccount.set(result.account)
                        this.checkAndSetActiveAccount()
                    },
                })
        } else {
            this.authService.loginRedirect(authRequest as RedirectRequest)
        }
    }

    logout(): void {
        this.authService.logoutRedirect()
    }
}
